<script setup lang="ts">
import InputCard from '~/components/ui/input/InputCard.vue'
import LoaderResults from '~/components/pages/results/LoaderResults.vue'
import { useResultStore } from '~/store/result'
import { trackTabSelect, trackPurchaseView } from '~/tracking/resultEvents'
import { getNumbers } from '~/utils/numbers'
import {
	SHOW_EXTERNAL_LINK,
	// SHOW_PLAY_HISTORY,
	SHOW_SCAN_TICKET,
	SHOW_TAB_SCAN_TICKET_ALWAYS,
} from '~/config/featureFlags'
import { useAuthStore } from '~/store/auth'

defineOptions({
	name: 'TabResults',
})

const { t: $t } = useI18n()
const { $hasFeature: _$hasFeature } = useNuxtApp()
const $hasFeature = _$hasFeature ?? (() => false)
const resultStore = useResultStore()
const localePath = useLocalePath()
const authStore = useAuthStore()
const { loggedIn } = storeToRefs(authStore)
const route = useRoute()
const router = useRouter()

const tabSelect = ref<number>(1)
const isMobile = ref<boolean>(false)
const scrollContainer = ref<HTMLElement | null>(null)
const tabRefs = reactive<Record<number, HTMLElement>>({})

interface Event {
	country: string
	game_name: string
	is_random: boolean
	numbers: string
	hot_numbers: string
	cold_numbers: string
	prizes_combinations: string
	user_id?: number
	tab_name: string
}

const TAB_IDS = {
	ALL_RESULTS: 1,
	ANALYTICS: 2,
	LOTTERY_RESULTS: 3,
	HISTORY: 4,
	SCAN_TICKET: 5,
} as const

interface TabItem {
	id: number
	name: string
	route: string
	is_new?: boolean
}

const initialTabs: TabItem[] = [
	{ id: TAB_IDS.ALL_RESULTS, name: $t('All results'), route: '/results' },
	{ id: TAB_IDS.ANALYTICS, name: $t('Analytics'), route: '/results/analytics' },
	{ id: TAB_IDS.LOTTERY_RESULTS, name: $t('Lottery results'), is_new: true, route: '/results/lotteries' },
]

const getLogin = computed(() => loggedIn.value)

const tabs = computed<TabItem[]>(() => {
	const allTabs: TabItem[] = [...initialTabs]

	if ($hasFeature(SHOW_SCAN_TICKET)) {
		if ($hasFeature(SHOW_TAB_SCAN_TICKET_ALWAYS) || getLogin.value === true) {
			const tabScanTicket: TabItem = {
				id: TAB_IDS.SCAN_TICKET,
				name: $t('Scan Ticket'),
				is_new: true,
				route: '/results/scan',
			}
			allTabs.splice(1, 0, tabScanTicket)
			const lotteryTab = allTabs.find((tab) => tab.id === TAB_IDS.LOTTERY_RESULTS)
			if (lotteryTab) lotteryTab.is_new = false
		}
	}

	// if ($hasFeature(SHOW_PLAY_HISTORY)) {
	// 	const tabHistory: TabItem = { id: TAB_IDS.HISTORY, name: $t('Plays history'), route: '/results/history' }
	// 	allTabs.push(tabHistory)
	// }

	return allTabs
})

const getClassContainer = computed(() => {
	const isTabAnalytics = route.path.includes('/results/analytics') ? 'md:bg-secondary-900' : null

	if ($hasFeature(SHOW_EXTERNAL_LINK)) return isTabAnalytics ?? '4xl:bg-secondary-900'
	return isTabAnalytics ?? 'xl:bg-secondary-900'
})

const scrollTabMobile = async (tabId: number) => {
	await nextTick()

	if (scrollContainer.value && tabRefs[tabId]) {
		const tabElement = tabRefs[tabId]

		const tabOffsetLeft = tabId >= 3 ? tabElement.offsetLeft - 30 : tabElement.offsetLeft

		scrollContainer.value.scrollTo({
			left: tabOffsetLeft,
			behavior: 'smooth',
		})
	}
}

const changeTab = (value: number) => {
	if (!isMobile.value) {
		scrollTabMobile(value)
	}
	const selected = tabs.value.find((tab) => tab.id === value)
	if (selected) {
		const params = { path: localePath(selected.route), query: route.query }
		router.push(params)
	}

	const userId = resultStore.user as { id: number } | null
	const countryLabel = resultStore.country as { label: string } | null
	const gameName = resultStore.lottery as { name: string } | null

	const { hot_numbers = [], cold_numbers = [], top_numbers = [] } = resultStore.analytics || {}
	const tab = tabs.value.find((item) => item.id === value)
	const eventContent: Event = {
		country: countryLabel?.label ?? '',
		game_name: gameName?.name ?? '',
		is_random: resultStore.is_random,
		numbers: resultStore.numbers ? [...getNumbers(resultStore.numbers)]?.join(', ') : '',
		hot_numbers: [...hot_numbers]?.join(', '),
		cold_numbers: [...cold_numbers]?.join(', '),
		prizes_combinations: top_numbers.map((item: any) => item.number)?.join(', '),
		tab_name: tab?.name ?? '',
	}
	if (userId && userId.id) {
		eventContent.user_id = userId.id
	}
	trackTabSelect(eventContent)
	if (!selected) return
	eventPurchaseView(selected.route)
}

const eventPurchaseView = (routeName: string) => {
	if (!$hasFeature(SHOW_EXTERNAL_LINK)) return
	if (routeName !== '/results') return

	trackPurchaseView()
}

const checkScreenSize = async () => {
	const value = window.innerWidth <= 640
	isMobile.value = !value
}

const normalize = (path: string) => path.replace(/\/+$/, '') || '/'

const setCurrentTab = () => {
	const currentPath = normalize(route.path)

	const matchedTab = tabs.value.reduce<TabItem | null>((best, tab) => {
		const localizedRoute = normalize(localePath(tab.route))
		const matches = currentPath === localizedRoute || currentPath.startsWith(`${localizedRoute}/`)

		if (!matches) return best
		if (!best) return tab

		const bestRoute = normalize(localePath(best.route))
		return localizedRoute.length > bestRoute.length ? tab : best
	}, null)

	if (!matchedTab) return

	tabSelect.value = matchedTab.id
}

watchEffect(() => {
	setCurrentTab()
})

onMounted(() => {
	if (typeof window === 'undefined') return
	checkScreenSize()
	window.addEventListener('resize', checkScreenSize)
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', checkScreenSize)
})
</script>

<template>
	<section
		class="bg-secondary-700 border-t border-primary-600 relative"
		:class="getClassContainer"
	>
		<div class="container-fluid relative">
			<div class="container-tabs absolute -top-[42px] left-0 w-full px-6 sm:px-0">
				<div
					ref="scrollContainer"
					class="overflow-x-auto pb-2 sm:ml-7"
				>
					<ul class="flex items-center whitespace-nowrap">
						<li
							v-for="tab in tabs"
							:key="tab.id"
							:ref="
								(el) => {
									if (el) tabRefs[tab.id] = el
								}
							"
						>
							<InputCard
								v-if="$hasFeature(SHOW_SCAN_TICKET)"
								v-model="tabSelect"
								:value="tab.id"
								type="radio"
								class="px-6 py-2 flex justify-center items-center text-white font-bold text-base"
								:class="
									tabSelect === tab.id
										? 'bg-primary-500 border border-primary-600 rounded-t-lg rounded-b-none'
										: 'bg-transparent hover:bg-transparent'
								"
								@change="changeTab"
							>
								{{ tab.name }}
								<div
									v-if="tab?.is_new"
									class="ml-2 px-1.5 rounded-md text-white text-xs font-medium bg-[linear-gradient(180deg,#4DD853_-3.07%,#233034_278.18%)]"
								>
									{{ $t('New') }}
								</div>
							</InputCard>
							<InputCard
								v-else
								v-model="tabSelect"
								:value="tab.id"
								type="radio"
								class="px-6 py-2 flex justify-center items-center text-white font-bold text-base"
								:class="
									tabSelect === tab.id
										? 'bg-success-600 rounded-t-lg rounded-b-none'
										: 'bg-transparent hover:bg-transparent'
								"
								@change="changeTab"
							>
								{{ tab.name }}
								<div
									v-if="tab?.is_new"
									class="ml-2 px-1.5 rounded-md bg-white text-xs text-primary-500"
								>
									{{ $t('New') }}
								</div>
							</InputCard>
						</li>
					</ul>
				</div>
			</div>
		</div>
		<div class="text-white min-h-dvh">
			<LoaderResults v-if="resultStore.loading" />
			<NuxtPage v-else />
		</div>
	</section>
</template>
